/**
 * Chat Sessions List Component
 * Table of chat sessions with expandable conversation view using HealthcareChatViewer
 */

import React, { useState } from 'react';
import { MessageSquare, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { ChatSession, ChatSessionFull } from '../types';
import { fetchChatConversation } from '../api';
import { HealthcareChatViewer } from './HealthcareChatViewer';

interface ChatSessionsListProps {
  chatSessions: ChatSession[];
}

const INITIAL_DISPLAY_COUNT = 3;

export const ChatSessionsList: React.FC<ChatSessionsListProps> = ({ chatSessions }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedSession, setExpandedSession] = useState<number | null>(null);
  const [sessionContent, setSessionContent] = useState<ChatSessionFull | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleViewConversation = async (sessionId: number) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setSessionContent(null);
      return;
    }

    setExpandedSession(sessionId);
    setLoadingContent(true);

    const { data } = await fetchChatConversation(sessionId);
    if (data) {
      setSessionContent(data);
    }
    setLoadingContent(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Chat Sessions</h3>
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
            {chatSessions.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6">
          {chatSessions.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No chat sessions in this time period
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {(showAll ? chatSessions : chatSessions.slice(0, INITIAL_DISPLAY_COUNT)).map(
                  (session) => (
                    <div key={session.id}>
                      <div
                        className={`p-4 rounded-lg border transition-colors ${
                          expandedSession === session.id
                            ? 'border-blue-200 bg-blue-50'
                            : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">
                              {session.title || `Chat Session #${session.id}`}
                            </h4>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-sm text-gray-500">
                                {formatDate(session.created_at)}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {session.message_count} messages
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleViewConversation(session.id)}
                            className={`ml-4 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                              expandedSession === session.id
                                ? 'bg-blue-600 text-white'
                                : 'text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            <Eye className="w-4 h-4" />
                            {expandedSession === session.id ? 'Hide' : 'View'}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Content - Healthcare Chat Viewer */}
                      {expandedSession === session.id && (
                        <div className="mt-2">
                          {loadingContent ? (
                            <div className="p-8 text-center text-gray-500">
                              Loading conversation...
                            </div>
                          ) : sessionContent ? (
                            <HealthcareChatViewer session={sessionContent} />
                          ) : null}
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>

              {/* See More / Show Less */}
              {chatSessions.length > INITIAL_DISPLAY_COUNT && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {showAll
                      ? `Show Less`
                      : `See More (${chatSessions.length - INITIAL_DISPLAY_COUNT} more sessions)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatSessionsList;
